import React, { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "react-bootstrap";
import _ from "lodash";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {
  FormProvider,
  useForm,
  useFormContext,
  Controller,
} from "react-hook-form";

import Hide from "assets/images/svg/hide.svg";
import Show from "assets/images/svg/show.svg";

import { errorClass } from "../classes";
import { json } from "helpers/data";
import {
  FormProps,
  InputFieldProps,
  MultiSelectProps,
  SelectFieldProps,
  TextAreaProps,
} from "./form.models";
import Input from "components/input-fields/input-fields";
import DateBox from "devextreme-react/date-box";
import DateIcon from "../../assets/images/svg/date_picker_blue.svg";

export function Form({
  defaultValues,
  formRef,
  onSubmit,
  children,
  ...props
}: FormProps) {
  const methods = useForm({ defaultValues });

  return (
    <FormProvider {...methods}>
      <form ref={formRef} onSubmit={methods.handleSubmit(onSubmit)} {...props}>
        {typeof children === "function" ? children(methods) : children}
      </form>
    </FormProvider>
  );
}

export function CheckBoxField({ id, label, ...props }: any) {
  const { t } = useTranslation();
  const { register } = useFormContext();

  return (
    <label className="inputcheck" htmlFor={id}>
      <input type="checkbox" id={id} {...props} {...register(id)} />
      {typeof label === "string" ? <span> {t(label) as string} </span> : label}
    </label>
  );
}

export function RadioButtonField({ id, label,name, rules = {},
  serverError = undefined, ...props }: any) {
  const { t } = useTranslation();
  const { register} = useFormContext();

  return (
    <>
      <Input type="radio" id={id} label={t(label) as string}  {...props} {...register(name)} name={name} isChecked={props.isChecked}   />
    </>
  );
}
export function Button({ isLoading,disabled, children, ...props }: any) {
  return (
    <button
      className={`d-flex align-items-center ${props.className || ""}`}
      disabled={isLoading || disabled}
      {...props}
    >
      {isLoading ? <Spinner animation="border" size="sm" /> : null} {children}
    </button>
  );
}

export function InputField({ id, label, rules = {}, type = "text", serverError = undefined, readonly = false,icon, ...props } : InputFieldProps) {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const { register, setValue, formState: { errors } } = useFormContext();
  const { onChange, ...properties } = register(id, rules);

  const handleShow = () => setShowPassword((s) => !s);

  // @ts-ignore
  const error = t(errors[id]?.message ?? '') || t(`server:${serverError ?? ""}`);

  return (
    <div className="input-div">
      <label className={`lapel-input ${error ? "error" : ""} ${readonly ? "readonly" : ""}`} htmlFor={id}>
        {typeof  label==="string" ?<span>{t(label) as string} : </span> : label}
        <input
          autoComplete="off"
          autoSave="off"
          id={id}
          disabled={readonly}
          type={showPassword ? "text" : type}
          onChange={(e) => {
            setValue(id, e.target.value?.trimStart() ?? "");
            onChange(e);
          }}
          {...properties}
          {...props}
        />
        {type === "password" ? (
          <img
            onClick={handleShow}
            src={showPassword ? Show : Hide}
            alt="hide"
          />
        ) : null}
      {icon && <>{icon}</>}
      </label>
      <span className={errorClass}>{error}</span>
      <span className="text-warning">{props.warning}</span>
    </div>
  );
}

export function DateField({id, label, rules = {}, type = "text", serverError = undefined, readonly = false,icon, ...props } : InputFieldProps) {
  const { t } = useTranslation();
  const { register,setValue, formState: { errors } } = useFormContext();
  const { onChange, ...properties } = register(id, rules);



  // @ts-ignore
  const error = t(errors[id]?.message ?? '') || t(`server:${serverError ?? ""}`);

  return (
      <div className="input-div">
        <label className={`lapel-input ${error ? "error" : ""} ${readonly ? "readonly" : ""}`} htmlFor={id}>
          {typeof  label==="string" ?<span>{t(label) as string} : </span> : label}
          <DateBox
              className='dxf-date-box'
              label={label as string}
              labelMode='hidden'
              stylingMode="outlined"
              placeholder="dd/mm/yyyy"
              type="date"
              displayFormat="dd/MM/yyyy"
              dropDownButtonRender={() => {
                  return <img src={DateIcon} className="me-2" alt='date-icon' />;
              }}
              onValueChange={value => setValue(id,value,{shouldValidate:true})}
              {...properties}
              {...props}
          />
        </label>
        <span className={errorClass}>{error}</span>
        <span className="text-warning">{props.warning}</span>
      </div>
  );
}

export function SelectField({
  id,
  label,
  rules = {},
  options = [],
  labelField = "label",
  valueField = "id",
  children,
  serverError,
    onChange,
  ...props
}: SelectFieldProps) {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const { t } = useTranslation();
  // @ts-ignore
  const error = t(errors[id]?.message ?? "") || t(`server:${serverError ?? ""}`);

  return (
    <div className="select-div">
      <label
        className={`lapel-input ${error ? "error" : ""} ${
          props.readonly ? "readonly" : ""
        }`}
      >
        {typeof label==="string"?<span>{label}:</span>:label}
        <Controller
          name={id}
          control={control}
          rules={rules}
          render={({ field: { ref, ...field } }) => {
            return (
              <Select autoComplete="off" className="inputSelect" {...field} onChange={(v) => {
                onChange?.(v);
                field.onChange(v);
              }} inputRef={ref} displayEmpty disabled={props.disabled}>
                <MenuItem value="" disabled />
                {!!options.length
                  ? options.map((option) => (
                      <MenuItem key={option.id} value={option[valueField]}>
                        {json(option[labelField])}
                      </MenuItem>
                    ))
                  : children}
              </Select>
            );
          }}
        />
      </label>
      <span className={errorClass}>{error}</span>
    </div>
  );
}

export function TextArea({ id, label, serverError, rules }: TextAreaProps) {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const { t } = useTranslation();
  // @ts-ignore
  const error = t(errors[id]?.message ?? "") || t(`server:${serverError ?? ""}`);

  return (
    <>
      <textarea
        className={"textarea " + (error ? "error" : "")}
        placeholder={label}
        {...register(id, rules)}
      />
      <span className={errorClass}>{error}</span>
    </>
  );
}

export function MultiSelect({
  field,
  options,
  labelField = "name",
  valueField = "id",
}: MultiSelectProps) {
  const { onChange, value, ref } = field;

  function handleChange(e: ChangeEvent<HTMLInputElement>, opt: object) {
    if (e.target.checked) {
      onChange([...value, _.get(opt, valueField)]);
    } else {
      onChange(value.filter((v: number) => v !== _.get(opt, valueField)));
    }
  }

  return (
    <div ref={ref}>
      {options.map((opt) => (
        <Input
          type="check"
          key={_.get(opt, valueField)}
          onChange={(e) => handleChange(e, opt)}
          label={json(_.get(opt, labelField) ?? "")}
        />
      ))}
    </div>
  );
}

